/*
    common reducers, shared between all pages

    NOTE: they can update the state with data received
    either from Buyer or Seller APIs
*/
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import type { KeyedReturnTypes, ThunkDispatcher } from '@gumtree/shared/src/model/generic.model';
import userSavedAds, { updateSavedAds } from '@gumtree/shared/src/reducers/saved-ads';
import type { SearchBar } from '@gumtree/web-bff/src/data/common/transform/search-bar';

import baseConfig, { updateBaseConfig } from './base-config';
import features, { updateFeatures } from './features';
import breakpoints, { updateBreakpoints } from './breakpoints';
import browseLinks, { updateBrowseLinks } from './browse-links';
import popularSearches, { updatePopularSearches } from './popular-searches';
import comScore, { updateComScore } from './com-score';
import categoryNav, { updateCategoryNav } from './category-nav';
import socialData, { updateSocialData } from './social-data';
import notifications from './notifications';
import userData, { updateUserData } from './user-data';
import userType, { updateUserType } from './user-type';
import tracking, { updateTracking } from './tracking';
import unreadMessages from './unread-messages';
import loginModal from './login-modal';
import request from './request';
import url from '../../common/url-reducer';
import searchBar from '../../header/search-bar/reducers';
import {
    setG5SParams,
    setInitialKeyword,
    setInitialLocation,
    setKeyword,
    setCategorySearch,
} from '../../header/search-bar/search-bar-actions';

export const updateCommonData =
    ({
        baseConfig,
        breakpoints,
        browseLinks,
        popularSearches,
        categoryNav,
        features,
        socialData,
        tracking,
        userData,
        userSavedAds,
        userType,
        comScore,
        urlQueryParams = undefined,
        searchBar,
    }: {
        baseConfig: any;
        breakpoints: any;
        browseLinks: any;
        popularSearches: any;
        categoryNav: any;
        features: any;
        socialData: any;
        tracking: any;
        userData: any;
        userSavedAds: any;
        userType: any;
        comScore: any;
        urlQueryParams?: unknown;
        searchBar: SearchBar;
    }) =>
    (dispatch) => {
        dispatch(updateBaseConfig({ ...baseConfig, urlQueryParams }));
        dispatch(updateBreakpoints(breakpoints));
        dispatch(updateBrowseLinks(browseLinks));
        dispatch(updatePopularSearches(popularSearches));
        dispatch(updateComScore(comScore));
        dispatch(updateFeatures(features));
        dispatch(updateCategoryNav(categoryNav));
        dispatch(updateSocialData(socialData));
        dispatch(updateTracking(tracking));
        dispatch(updateUserData(userData));
        dispatch(updateUserType(userType));
        dispatch(updateSavedAds(userSavedAds));
        dispatch(setInitialKeyword(searchBar.keyword.value));
        dispatch(setKeyword(searchBar.keyword.value));
        dispatch(setInitialLocation(searchBar.location.value));
        dispatch(setCategorySearch(searchBar.searchCategory.searchCategory));
        dispatch(setG5SParams(searchBar.suggestionServiceG5SParams));
    };

const defaultReducer = (state = {}) => state;

const index = {
    baseConfig,
    breakpoints,
    browseLinks,
    popularSearches,
    categoryNav,
    comScore,
    features,
    userData,
    userType,
    socialData,
    searchBar,
    tracking,
    url, // TODO: remove this when tidying up redirect to other pages (shell/content.tsx)
    notifications,
    userSavedAds,
    imageSupport: defaultReducer,
    request,
    loginModal,
    unreadMessagesCount: unreadMessages,
};

export type ShellState = KeyedReturnTypes<typeof index>;
export type ShellUseDispatch = ThunkDispatch<ShellState, any, AnyAction>;
export type ShellThunk<Payload, Return = any> = ThunkDispatcher<ShellState, Payload, Return>;

export default index;
